import { pasteHandler } from '@wordpress/blocks';
import {
	isEmpty,
	insert,
	create,
	getActiveFormats,
	privateApis as richTextPrivateApis,
} from '@wordpress/rich-text';
import { isURL } from '@wordpress/url';
import { privateApis as composePrivateApis } from '@wordpress/compose';
import { store as blockEditorStore } from '../../../store';
import { addActiveFormats } from '../utils';
import { getPasteEventData } from '../../../utils/pasting';
import { unlock } from '../../../lock-unlock';
import { getDropPasteProps, isTextDrop } from './drag-and-drop';

const { subscribeDelegatedListener } = unlock( composePrivateApis );
const { ownsSelection } = unlock( richTextPrivateApis );

/** @typedef {import('@wordpress/rich-text').RichTextValue} RichTextValue */

/**
 * Creates the paste listener of a field, which also inserts text dropped on
 * it.
 *
 * @param {Object}      props   Rich text props.
 * @param {HTMLElement} element The field.
 *
 * @return {(event: Event) => void} The listener.
 */
export function createPasteListener( props, element ) {
	return function _onPaste( event ) {
		// A drop pastes at the drop point.
		const pasteProps =
			event.type === 'drop'
				? getDropPasteProps( props.current, element, event )
				: props.current;

		if ( ! pasteProps ) {
			return;
		}

		const {
			disableFormats,
			onChange,
			value,
			formatTypes,
			tagName,
			onReplace,
			__unstableEmbedURLOnPaste,
			preserveWhiteSpace,
			pastePlainText,
			registry,
		} = pasteProps;

		// The event listener is attached to the window, so we need to check if
		// the target is the element or inside the element. When the editable
		// wrapper holds focus (the selected block supports `editableRoot`),
		// the event targets the wrapper instead; the element then owns the
		// paste when it contains the selection.
		if (
			! element.contains( event.target ) &&
			! ownsSelection( element )
		) {
			return;
		}

		if ( event.defaultPrevented ) {
			return;
		}

		const { clipboardData = event.dataTransfer } = event;
		const pasteData = getPasteEventData( { clipboardData } );

		// Some browsers don't support `clipboardData` and paste plain text on
		// their own, so the event has to be left alone for them.
		if ( ! pasteData ) {
			return;
		}

		event.preventDefault();

		const { plainText, html, files } = pasteData;

		// Rich text can only paste text; files are placed by the writing flow.
		if ( files.length ) {
			return;
		}

		// Allows us to ask for this information when we get a report.
		// `pasteHandler` also logs this, but we're not using `pasteHandler` in
		// every case.
		window.console.log( 'Received HTML (RichText):\n\n', html );
		window.console.log( 'Received plain text (RichText):\n\n', plainText );

		if ( disableFormats ) {
			onChange( insert( value, plainText ) );
			return;
		}

		const isInternal = clipboardData.getData( 'rich-text' ) === 'true';

		function pasteInline( content ) {
			const transformed = formatTypes.reduce(
				( accumulator, { __unstablePasteRule } ) => {
					// Only allow one transform.
					if ( __unstablePasteRule && accumulator === value ) {
						accumulator = __unstablePasteRule( value, {
							html,
							plainText,
						} );
					}

					return accumulator;
				},
				value
			);
			if ( transformed !== value ) {
				onChange( transformed );
			} else {
				const valueToInsert = create( { html: content } );
				addActiveFormats( valueToInsert, getActiveFormats( value ) );
				onChange( insert( value, valueToInsert ) );
			}
		}

		// If the data comes from a rich text instance, we can directly use it
		// without filtering the data. The filters are only meant for externally
		// pasted content and remove inline styles.
		if ( isInternal ) {
			pasteInline( html );
			return;
		}

		if ( pastePlainText ) {
			const valueToInsert = create( { text: plainText } );
			addActiveFormats( valueToInsert, getActiveFormats( value ) );
			onChange( insert( value, valueToInsert ) );
			return;
		}

		let mode = 'INLINE';

		const trimmedPlainText = plainText.trim();

		if (
			__unstableEmbedURLOnPaste &&
			isEmpty( value ) &&
			isURL( trimmedPlainText ) &&
			// For the link pasting feature, allow only http(s) protocols.
			/^https?:/.test( trimmedPlainText )
		) {
			mode = 'BLOCKS';
		}

		const content = pasteHandler( {
			HTML: html,
			plainText,
			mode,
			tagName,
			preserveWhiteSpace,
		} );

		if ( typeof content === 'string' ) {
			pasteInline( content );
			return;
		}

		if ( ! content.length || ! onReplace || ! isEmpty( value ) ) {
			return;
		}

		// Record an intermediate paragraph-with-URL state so a single undo
		// after the URL → block transformation restores the pasted link.
		if ( mode === 'BLOCKS' ) {
			pasteInline( html );
			registry
				.dispatch( blockEditorStore )
				.__unstableMarkLastChangeAsPersistent();
		}

		onReplace( content, content.length - 1, -1 );
	};
}

export default ( props ) => ( element ) => {
	const { defaultView } = element.ownerDocument;

	// Attach the listener to the window so parent elements have the chance to
	// prevent the default behavior.
	return subscribeDelegatedListener(
		defaultView,
		'paste',
		createPasteListener( props, element )
	);
};

/**
 * Inserts text dropped on the field the way the field would on paste, as one
 * undo level.
 *
 * @param {Object} props Rich text props.
 */
export const drop = ( props ) => ( element ) => {
	const { ownerDocument } = element;
	const onPaste = createPasteListener( props, element );

	// The block drop zones cancel every drag over them, which hides the
	// browser's drop caret and shows the block insertion point instead. An
	// empty field keeps the drop zone, which shows the block is replaced.
	function onDragOver( event ) {
		if (
			element.isContentEditable &&
			! isEmpty( props.current.getValue() ) &&
			isTextDrop( ownerDocument, event.dataTransfer )
		) {
			event.stopPropagation();
		}
	}

	function onDrop( event ) {
		props.current.registry.batch( () => onPaste( event ) );
	}

	element.addEventListener( 'dragenter', onDragOver );
	element.addEventListener( 'dragover', onDragOver );
	element.addEventListener( 'drop', onDrop );
	return () => {
		element.removeEventListener( 'dragenter', onDragOver );
		element.removeEventListener( 'dragover', onDragOver );
		element.removeEventListener( 'drop', onDrop );
	};
};
